import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { Loader2, Mail, Phone, User, Calendar } from "lucide-react";

export default function Dashboard() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const { fetchContacts: fetchContactsAPI } = await import("@/utils/api");
      const data = await fetchContactsAPI();
      setContacts(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setError("Failed to load contact submissions");
      toast.error("Failed to load contact submissions");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--utu-bg))] text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--utu-gold))]" />
          <p className="text-white/70">Loading contact submissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--utu-bg))] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchContacts}
            className="px-4 py-2 bg-[hsl(var(--utu-gold))] text-black rounded-lg font-semibold hover:opacity-90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--utu-bg))] text-white">
      <div className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-semibold tracking-tight text-white mb-2">
            Contact Submissions Dashboard
          </h1>
          <p className="text-white/70">
            View all contact requests from the UTU website
          </p>
          <div className="mt-4">
            <Badge className="bg-[hsl(var(--utu-gold))] text-black">
              {contacts.length} {contacts.length === 1 ? "submission" : "submissions"}
            </Badge>
          </div>
        </div>

        {contacts.length === 0 ? (
          <Card className="p-8 text-center border-white/10 bg-white/5 backdrop-blur-xl">
            <p className="text-white/70">No contact submissions yet.</p>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {contacts.map((contact) => (
              <Card
                key={contact.id}
                className="p-6 border-white/10 bg-white/5 backdrop-blur-xl hover:bg-white/8 transition-colors"
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-[hsl(var(--utu-gold))]" />
                      <h3 className="text-lg font-semibold text-white">
                        {contact.name || "No name"}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <Mail className="h-4 w-4 text-[hsl(var(--utu-gold))]" />
                      <a
                        href={`mailto:${contact.email}`}
                        className="hover:text-white transition-colors"
                      >
                        {contact.email}
                      </a>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <Phone className="h-4 w-4 text-[hsl(var(--utu-gold))]" />
                      <a
                        href={`tel:${contact.phone}`}
                        className="hover:text-white transition-colors"
                      >
                        {contact.phone}
                      </a>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-white/55">
                      <Calendar className="h-3 w-3 text-[hsl(var(--utu-gold))]" />
                      <span>{formatDate(contact.timestamp)}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-8 flex justify-end">
          <button
            onClick={fetchContacts}
            className="px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}
